import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase-client';
import { Coins, AlertCircle, Wallet } from 'lucide-react';
import { getBalance, getPositions } from '../lib/api';

type MyAssetsProps = {
  showPnL?: boolean;
};

export default function MyAssets({ showPnL = true }: MyAssetsProps) {
  const [assets, setAssets] = useState<any>(null);
  const [positions, setPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState<number>(0);
  const [exchangeConnected, setExchangeConnected] = useState(false);

  useEffect(() => {
    loadAssets();
    loadCredits();

    const subscription = supabase
      .channel('balance_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        loadAssets();
        loadCredits();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadCredits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('credits')
        .eq('id', user.id)
        .single();

      setCredits(profile?.credits || 0);
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  };

  const loadAssets = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('trading_account, trading_enabled')
        .eq('id', user.id)
        .single();

      if (!profile?.trading_account?.api_key || !profile?.trading_enabled) {
        setExchangeConnected(false);
        setAssets({
          totalBalance: 0,
          availableBalance: 0,
          inTrade: 0,
          unrealizedPnL: 0
        });
        return;
      }

      setExchangeConnected(true);

      const balance = await getBalance('bybit', user.id, 'USDT');
      const positions = await getPositions('bybit', user.id);

      const inTrade = positions.reduce((sum, pos) => 
        sum + Math.abs(pos.size * pos.entryPrice), 0);
      const unrealizedPnL = positions.reduce((sum, pos) => 
        sum + pos.unrealizedPnL, 0);

      setAssets({
        totalBalance: balance + unrealizedPnL,
        availableBalance: balance,
        inTrade,
        unrealizedPnL
      });
      setPositions(positions);
    } catch (error) {
      console.error('Error loading assets:', error);
      setAssets({
        totalBalance: 0,
        availableBalance: 0,
        inTrade: 0,
        unrealizedPnL: 0
      });
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-black/20 rounded-lg p-4">
          <p className="text-sm text-gray-400">Total Balance</p>
          <p className="text-2xl font-bold text-white">${assets?.totalBalance.toLocaleString()}</p>
        </div>
        <div className="bg-black/20 rounded-lg p-4">
          <p className="text-sm text-gray-400">Available</p>
          <p className="text-2xl font-bold text-white">${assets?.availableBalance.toLocaleString()}</p>
        </div>
        <div className="bg-black/20 rounded-lg p-4">
          <p className="text-sm text-gray-400">In Trade</p>
          <p className="text-2xl font-bold text-white">${assets?.inTrade.toLocaleString()}</p>
        </div>
        <div className="bg-black/20 rounded-lg p-4">
          <p className="text-sm text-gray-400">Unrealized P&L</p>
          <p className={`text-2xl font-bold ${assets?.unrealizedPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {assets?.unrealizedPnL >= 0 ? '+' : ''}{assets?.unrealizedPnL.toLocaleString()}
          </p>
        </div>
        <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Coins className="h-5 w-5 text-cyan-400" />
            <p className="text-sm text-gray-400">Credits</p>
          </div>
          <p className="text-2xl font-bold text-cyan-400">{credits.toLocaleString()}</p>
        </div>
      </div>

      {positions.length > 0 && (
        <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-4 border border-blue-500/20">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center space-x-2">
            <Wallet className="h-5 w-5 text-cyan-400" />
            <span>Open Positions</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Symbol</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Side</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Size</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Entry Price</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Mark Price</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">PnL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {positions.map((position, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-white">{position.symbol}</td>
                    <td className={`px-4 py-2 ${position.side === 'Buy' ? 'text-green-400' : 'text-red-400'}`}>
                      {position.side}
                    </td>
                    <td className="px-4 py-2 text-gray-300">{position.size}</td>
                    <td className="px-4 py-2 text-gray-300">${position.entryPrice.toLocaleString()}</td>
                    <td className="px-4 py-2 text-gray-300">${position.markPrice.toLocaleString()}</td>
                    <td className={`px-4 py-2 ${position.unrealizedPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {position.unrealizedPnL >= 0 ? '+' : ''}{position.unrealizedPnL.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!exchangeConnected && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-300">
            <p className="font-medium text-yellow-500 mb-1">Exchange Not Connected</p>
            <p>
              Please configure your exchange API key and secret in the settings page to view your real-time trading balances and performance.
              Both API key and secret are required.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
